'use client';

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { cn } from '@/lib/utils';
import { useScrollReveal } from '../../hooks/useScrollReveal';

// IntersectionObserver capability check — SSR-safe via useSyncExternalStore (no
// setState-in-effect; react-hooks/set-state-in-effect). Browser capability never
// changes at runtime, so subscribe is a no-op. getServerSnapshot=true assumes IO
// is available pre-hydration (true for every evergreen browser this site targets)
// so the fallback branch only ever engages post-hydration, on a real capability gap.
function subscribeIOSupport() {
  return () => {};
}
function getIOSupportSnapshot() {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}
function getIOSupportServerSnapshot() {
  return true;
}

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  direction?: 'up' | 'left' | 'right';
  delay?: number;
  threshold?: number;
}

/**
 * ScrollReveal — wraps children in a scroll-triggered reveal animation.
 *
 * Reduced motion (Judge 5 motion spec, finding 3 — 2026-07-10 landing rebuild):
 *   IO triggering stays alive; reduced motion is a CSS-level concern, not a JS
 *   bypass. This component always waits for the real intersection (or the
 *   genuine-failure fallback below) and applies the SAME `animate-reveal-*`
 *   class either way. globals.css's `@media (prefers-reduced-motion: reduce)`
 *   block then swaps that class's animation to a 320ms opacity-only fade
 *   (`reduced-motion-fade`, `--motion-reduced` token) — never an instant
 *   `opacity-100` snap, and never a permanently-dead reveal.
 *
 * IO-failure fallback (Judge 5 motion spec, finding 2 — 2026-07-10):
 *   The previous 4s watchdog armed unconditionally at mount for every instance,
 *   so anything below the fold silently force-revealed 4s after page load
 *   regardless of scroll position — deleting most entrances for anyone who
 *   pauses on the hero. It now arms ONLY when IntersectionObserver is
 *   unavailable (fires immediately — there is no reason to wait 4s for an API
 *   that will never exist) or when observation has genuinely failed: the
 *   element is already within/near the viewport at the 4s mark yet IO never
 *   reported it. Content still below the fold at 4s is left alone — IO will
 *   reveal it normally when the user actually scrolls there.
 */
export default function ScrollReveal({
  children,
  className,
  direction = 'up',
  delay = 0,
  threshold = 0.15,
}: ScrollRevealProps) {
  const { ref, isVisible } = useScrollReveal({ threshold });
  const [hasAnimated, setHasAnimated] = useState(false);

  const ioSupported = useSyncExternalStore(
    subscribeIOSupport,
    getIOSupportSnapshot,
    getIOSupportServerSnapshot
  );

  // Genuine-failure fallback: only ever forces visibility when IO can't or
  // didn't do its job — never a blanket per-element mount timer.
  const [timerExpired, setTimerExpired] = useState(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isVisible) return; // Already resolved via a real intersection — nothing to watch for.

    if (!ioSupported) {
      // IntersectionObserver doesn't exist in this environment — it will never fire.
      // Force visible now; waiting 4s here serves no purpose.
      setTimerExpired(true);
      return;
    }

    // Healthy observer: arm the 4s safety net, but only force-reveal at expiry if
    // the element was already on/near screen and IO still never reported it (a
    // genuine failure) — not simply because the user hasn't scrolled there yet.
    fallbackTimerRef.current = setTimeout(() => {
      const rect = ref.current?.getBoundingClientRect();
      const nearViewport = !!rect && rect.top < window.innerHeight && rect.bottom > 0;
      if (nearViewport) {
        setTimerExpired(true); // setState inside an async callback — not a sync effect write.
      }
    }, 4000);

    return () => {
      if (fallbackTimerRef.current !== null) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [ioSupported, isVisible, ref]);

  const handleAnimationEnd = useCallback(() => setHasAnimated(true), []);

  const animationClass = {
    up: 'animate-reveal-up',
    left: 'animate-reveal-left',
    right: 'animate-reveal-right',
  }[direction];

  // isVisible (real IO) or timerExpired (genuine-failure fallback) both resolve
  // to the SAME animation class — reduced motion downgrades it via CSS, it never
  // branches in JS. Non-marketing consumers (preview feature) keep the identical
  // IO-triggered entrance behaviour they had before this fix.
  const resolvedClass = (() => {
    if (hasAnimated) return 'opacity-100';
    if (isVisible || timerExpired) return animationClass;
    return 'opacity-0';
  })();

  // Only apply animationDelay when the animation will actually run.
  const style =
    !hasAnimated && (isVisible || timerExpired) ? { animationDelay: `${delay}ms` } : undefined;

  return (
    <div
      ref={ref}
      className={cn(resolvedClass, className)}
      style={style}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
}
